<script setup lang="ts">
import { formatAuthorAndDate } from "#imports"
import { IffOperationNoteEditor } from "#components"
import type { components } from "~/assets/api/openapi"

const slideOver = useSlideover()
const operationStore = useOperationStore()
const { d, t, te } = useI18n()

const currentOperationType = computed<string>(
  () => operationStore.operation!.operationType
)

const proposalStatusDisplay = computed<string>(() => {
  const statusInfo = operationStore.findFormDataValueByTextKey<
    components["schemas"]["Multiple"]
  >(
    `${currentOperationType.value}.applicationDataFields.proposalStatusInfo`
  )?.value

  if (!statusInfo) return ""

  const [status, statusDate] = statusInfo as [
    components["schemas"]["LocaleValue"],
    components["schemas"]["FormattedDate"],
  ]

  if (!status.textKey) return ""

  const statusText = t(status.textKey)
  const dateValue = statusDate.value

  return dateValue ? `${statusText}, ${d(dateValue, "date")}` : statusText
})

const status = computed<string>(() => {
  if (
    !operationStore.operation?.metaData?.status ||
    !proposalStatusDisplay.value
  ) {
    return ""
  }

  return `${proposalStatusDisplay.value} | ${t(
    `iff.components.status-tag-detailed.${operationStore.operation.metaData.status.toLowerCase()}`
  )}`
})

const currentResponsibleUser = computed<string>(() => {
  const responsibleUser = operationStore.operation?.metaData.responsibleUser
  const { firstName, lastName } = responsibleUser || {}
  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  }
  return firstName || lastName || "-"
})

const editor = computed<string | undefined>(() => {
  const user = operationStore.operation?.metaData.modifiedUser

  if (user) {
    const { firstName, lastName } = user

    if (firstName && lastName) return `${firstName} ${lastName}`
    if (firstName) return firstName
    if (lastName) return lastName
  }

  return undefined
})

const lastEditedText = computed<string>(() => {
  if (operationStore.operation?.metaData.modifiedDate) {
    return t("iff.components.operation-metadata-last-edited.text", {
      DATE: `${d(Date.parse(operationStore.operation.metaData.modifiedDate), "date-time")}`,
      EDITOR: editor.value ?? "-",
    })
  } else {
    return t("iff.components.operation-metadata-last-edited.fallback-text")
  }
})

const editMemo = () => {
  if (operationStore.operation?.id) {
    slideOver.open(IffOperationNoteEditor, {
      operationId: operationStore.operation.id,
      operationNote: operationStore.operation?.operationNote,
      onOperationChange(operation: components["schemas"]["Operation"]) {
        operationStore.operation = operation
      },
    })
  }
}

const operationNoteAuthorAndDate = computed<string>(() => {
  const operationNote = operationStore.operation?.operationNote
  if (!operationNote?.metaData) return "-"

  const user =
    operationNote.metaData.modifiedUser || operationNote.metaData.createdUser
  const date =
    operationNote.metaData.modifiedDate || operationNote.metaData.createdDate
  if (!user) return "-"

  const { firstName, lastName } = user

  return formatAuthorAndDate(firstName || "", lastName || "", date)
})
</script>

<template>
  <section class="py-6 px-3" v-if="operationStore.operation">
    <IffHeadline type="h2" class="mb-3">
      {{ $t("iff.components.operation-metadata.headline") }}
    </IffHeadline>

    <IffCopyText size="smaller" class="mb-3">
      <span v-if="te('iff.components.operation-metadata.created-by-prefix')">
        {{ $t("iff.components.operation-metadata.created-by-prefix") }}
      </span>
      {{
        `${operationStore.operation?.metaData.createdUser?.firstName} ${
          operationStore.operation?.metaData.createdUser?.lastName
        }, ${d(Date.parse(operationStore.operation?.metaData.createdDate ?? ""), "date-time")}`
      }}
    </IffCopyText>

    <div class="grid grid-cols-2 gap-3 mb-5">
      <IffInfoBox
        :label="t('iff.components.operation-metadata.status')"
        :text="status"
        icon="i-ic-outline-hourglass-top"
      />

      <IffInfoBox
        :label="t('iff.components.operation-metadata-last-edited.label')"
        :text="lastEditedText"
        icon="i-ic-outline-edit"
      />

      <IffInfoBox
        :label="t('iff.components.operation-metadata.responsible-user')"
        :text="currentResponsibleUser"
        icon="i-ic-outline-face"
      />

      <IffInfoBox
        :label="t('iff.components.operation-metadata.responsible-organisation')"
        :text="
          operationStore.operation?.metaData.responsibleOrganisation?.name ??
          '-'
        "
        icon="i-ic-outline-account-balance"
      />
    </div>

    <section
      v-if="!operationStore.operation.operationNote?.note"
      class="info-box border-solid border-2 rounded-xl border-gray-200 mb-5 p-4"
    >
      <div class="flex gap-1 mb-1 w-full items-center">
        <UIcon name="i-ic-outline-insert-comment" class="mr-1" />
        <h2 class="text-sm">
          {{ t("iff.components.operation-metadata.memo") }}
        </h2>
      </div>

      <div class="flex justify-center">
        <UButton
          variant="outline"
          @click="editMemo()"
          icon="i-ic-add"
          class="bg-white"
        >
          {{ t("iff.components.operation-metadata.memo-add") }}
        </UButton>
      </div>
    </section>

    <section
      v-else
      class="info-box border-solid border-2 rounded-xl border-gray-200 mb-5 p-4 bg-warning-100"
    >
      <div class="flex gap-1 mb-1 w-full items-center">
        <UIcon name="i-ic-outline-insert-comment" class="mr-1" />
        <h2 class="text-sm">
          {{ t("iff.components.operation-metadata.memo") }}
        </h2>
        <UButton
          @click="editMemo()"
          icon="i-ic-outline-edit"
          size="sm"
          color="primary"
          variant="ghost"
          :title="t('iff.components.operation-metadata.memo-edit')"
          :aria-label="t('iff.components.operation-metadata.memo-edit')"
        />
      </div>

      <IffCopyText size="small">
        <span class="italic">
          {{ operationStore.operation.operationNote.note }}
        </span>
        <span class="block mt-1 text-gray-500 text-xs">
          {{ operationNoteAuthorAndDate }}
        </span>
      </IffCopyText>
    </section>

    <div>
      <IffOperationCommunicationCenterEmbed />
    </div>
  </section>
</template>
